import {
  DownloadOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  PrinterOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Flex, type MenuProps, Tag, notification } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  downloadBlob,
  filenameFromContentDisposition
} from "../../../../utils/download-file.ts"
import { exportReport } from "../../api/services.ts"
import type { ReportExportFormat } from "../../types/collab.types.ts"

interface ReportExportMenuProps {
  reportId: string
  reportName: string
  onPrint: () => void
}

const EXTENSION: Record<ReportExportFormat, string> = {
  pdf: "pdf",
  xlsx: "xlsx",
  csv: "csv"
}

export const ReportExportMenu = ({
  reportId,
  reportName,
  onPrint
}: ReportExportMenuProps) => {
  const { t } = useTranslation()
  const [pending, setPending] = useState<ReportExportFormat | null>(null)

  const runExport = async (format: ReportExportFormat) => {
    setPending(format)
    try {
      const response = await exportReport(reportId, format)
      if (!response.ok || !response.data) {
        notification.error({ message: t("reports.export.failed") })
        return
      }
      const filename =
        filenameFromContentDisposition(response.headers) ??
        `${reportName}.${EXTENSION[format]}`
      downloadBlob(response.data as Blob, filename)
    } catch {
      notification.error({ message: t("reports.export.failed") })
    } finally {
      setPending(null)
    }
  }

  const items: MenuProps["items"] = [
    {
      key: "pdf",
      icon: <FilePdfOutlined />,
      label: t("reports.export.pdf"),
      onClick: () => runExport("pdf")
    },
    {
      key: "xlsx",
      icon: <FileExcelOutlined />,
      label: t("reports.export.excel"),
      onClick: () => runExport("xlsx")
    },
    {
      key: "csv",
      icon: <FileTextOutlined />,
      label: t("reports.export.csv"),
      onClick: () => runExport("csv")
    },
    {
      key: "png",
      icon: <FileImageOutlined />,
      label: (
        <Flex align="center" justify="space-between" gap="small">
          <span>{t("reports.export.png")}</span>
          <Tag className="m-0" color="default">
            {t("reports.export.comingSoon")}
          </Tag>
        </Flex>
      ),
      onClick: () => undefined
    },
    { type: "divider" },
    {
      key: "print",
      icon: <PrinterOutlined />,
      label: t("reports.export.print"),
      onClick: onPrint
    }
  ]

  return (
    <Dropdown menu={{ items }} trigger={["click"]}>
      <Button icon={<DownloadOutlined />} loading={pending !== null}>
        {t("reports.toolbar.export")}
      </Button>
    </Dropdown>
  )
}
